import React, { useEffect, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import SlideOutSidebar from "../../ui-components/slide-out-sidebar/slideOutSidebar";
import Tag from "../../ui-components/molecules/tag/tag";
import { TagColor } from "../../app/data/common/tagColor";
import { LinehaulTrip } from "../../app/data/linehaul-mileage/models";
import { useDispatch } from "react-redux";
import { assignDriver, fetchLinehaulLanes, fetchLinehaulTripActivity, resetLinehaulLanes, reviewTrip } from "../../slices/linehaul-mileage/linehaulMileageSlice";
import { toast } from "react-toastify";
import mixpanel from "mixpanel-browser";
import { SelectDriver } from "./selectDriver";
import { AddLane } from "./addLane";
import { AddAccessorial } from "./addAccessorial";
import { LaneAssignment } from "./laneAssignment";
import { TripSettlementTab } from "./tripSettlementTab";
import { TripOperationsTab } from "./tripOperationsTab";
import { LinehaulTripActivityContent } from "./linehaulTripActivity";
import "../../ui-components/tabs/tabs.scss";

export interface TripDetailsProps {
  trip?: LinehaulTrip,
  onClose: any;
  show: boolean;
}

export interface Driver {
  name: string;
  id: string;
}

const SETTLEMENT_TAB = 0;

export const TripDetails: React.FC<TripDetailsProps> = (props) => {
  const [activeTab, setActiveTab] = useState(SETTLEMENT_TAB);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [addNewLane, setAddNewLane] = useState(false);
  const [addNewAccessorial, setAddNewAccessorial] = useState(false);
  const [assignLane, setAssignLane] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!props.show) {
      setActiveTab(SETTLEMENT_TAB);
      setShowDriverModal(false);
      setAddNewAccessorial(false);
      setAddNewLane(false);
      setAssignLane(false);
    }
  }, [props.show])

  useEffect(() => {
    setActiveTab(SETTLEMENT_TAB);
    setShowDriverModal(false);
    setAddNewAccessorial(false);
    setAddNewLane(false);
    setAssignLane(false);
  }, [props.trip])

  useEffect(() => {
    dispatch(fetchLinehaulLanes(props.trip?.originTerminalNumber, props.trip?.destinationTerminalNumber));
    return () => {
      dispatch(resetLinehaulLanes());
    }
  }, [props.trip?.originTerminalNumber, props.trip?.destinationTerminalNumber, dispatch])

  useEffect(() => {
    if (!props.trip?.tripId) return;
    dispatch(fetchLinehaulTripActivity(props.trip.tripId));
  }, [props.trip?.tripId, props.trip?.lane, props.trip?.exceptions, dispatch])

  const onReviewTrip = () => {
    dispatch(
      reviewTrip(props.trip?.tripId!,
        false,
        () => {
          toast.info("Lane reviewed")
          mixpanel.track("linehaul_mileage:mileage_reviewed");
        },
        (error: string) => toast.error(error || "Something went wrong")
      )
    )
  }

  const onUnReviewTrip = () => {
    dispatch(
      reviewTrip(props.trip?.tripId!,
        true,
        () => toast.info("Lane unreviewed"),
        (error: string) => toast.error(error || "Something went wrong")
      )
    )
  }

  const onDriverAssign = (driverId: string) => {
    dispatch(
      assignDriver(
        props.trip?.tripId!, 
        driverId, 
        () => {
          toast.info("Driver assigned");
          setShowDriverModal(false);
          mixpanel.track("linehaul_mileage:driver_assigned", {
            trip_id: props.trip?.tripId,
            driver_id: driverId,
          });
        })
    )
  }

  return (
    <SlideOutSidebar header="Trip Settlement Review" show={props.show} onClose={props.onClose}>
      <div className="xgs-lmi__details">
        <div className="xgs-lmi__title">
          Manifest: {props.trip?.manifestNumber}
          {
            props.trip?.isReviewed ? (
              <Tag mods={{color: TagColor.GREEN}}>REVIEWED</Tag>
            ) : (
              <Tag mods={{color: TagColor.RED}}>NOT REVIEWED</Tag>
            )
          }
        </div>
        <Tabs className="xgs-lmi__tabs" selectedIndex={activeTab} onSelect={setActiveTab}>
          <TabList>
            <Tab>Settlement</Tab>
            <Tab>Operations</Tab>
            <Tab>Activity History</Tab>
          </TabList>
          <TabPanel>
            <TripSettlementTab trip={props.trip} />
          </TabPanel>
          <TabPanel>
            <TripOperationsTab
              trip={props.trip}
              onShowDriverModal={() => setShowDriverModal(true)}
              onAssignLane={() => setAssignLane(true)}
              onAddAccessorial={() => setAddNewAccessorial(true)}
              onReviewTrip={onReviewTrip}
              onUnReviewTrip={onUnReviewTrip}
            />
          </TabPanel>
          <TabPanel>
            <LinehaulTripActivityContent />
          </TabPanel>
        </Tabs>
      </div>
      {!!addNewAccessorial && <AddAccessorial
        show={addNewAccessorial}
        closeModal={() => setAddNewAccessorial(false)}
        tripId={props.trip?.tripId!}
      />}
      {!!showDriverModal && <SelectDriver 
        closeModal={() => setShowDriverModal(false)}
        onDriverSelect={onDriverAssign}
        show={showDriverModal}
      />}
      {!!addNewLane && <AddLane
        originTerminalNumber={props.trip?.originTerminalNumber} 
        destinationTerminalNumber={props.trip?.destinationTerminalNumber}
        show={addNewLane}
        closeModal={() => setAddNewLane(false)}
      />}
      {!!assignLane && <LaneAssignment
        originTerminalNumber={props.trip?.originTerminalNumber!}
        destinationTerminalNumber={props.trip?.destinationTerminalNumber!}
        show={assignLane}
        closeModal={() => setAssignLane(false)}
        trip={props.trip}
      />}
    </SlideOutSidebar>
  )
}
